import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';

// Define the type for app settings
export type AppSetting = {
  id: number;
  type: string;
  value: string;
  isActive: boolean;
  isDefault?: boolean;
  tenantId?: number | null;
  displayOrder: number;
  createdAt: string | null;
  updatedAt: string | null;
};

// Define the context shape
interface AppSettingsContextType {
  // Settings by type
  categories: AppSetting[];
  billingCycles: AppSetting[];
  statuses: AppSetting[];
  cardTypes: AppSetting[];
  cardProviders: AppSetting[];
  currencies: AppSetting[];
  
  // Helper functions
  getSettingsByType: (type: string) => AppSetting[];
  isLoading: boolean;
  error: Error | null;
}

// Create the context with default values
const AppSettingsContext = createContext<AppSettingsContextType>({
  categories: [],
  billingCycles: [],
  statuses: [],
  cardTypes: [],
  cardProviders: [],
  currencies: [],
  getSettingsByType: () => [],
  isLoading: false,
  error: null
});

// Settings types we're interested in
const SETTING_TYPES = [
  'category',
  'billingCycle',
  'status',
  'cardType',
  'cardProvider',
  'currency'
];

// Provider component that fetches and provides app settings
export function AppSettingsProvider({ children }: { children: ReactNode }) {
  // Store the settings grouped by type
  const [settingsByType, setSettingsByType] = useState<Record<string, AppSetting[]>>({});
  const [error, setError] = useState<Error | null>(null);
  
  // Individual fetch queries for each setting type
  const queryResults = SETTING_TYPES.map(type => {
    return useQuery({
      queryKey: ['/api/app-settings', type],
      queryFn: async () => {
        console.log(`Making API request: GET /api/app-settings?type=${type}`);
        const response = await fetch(`/api/app-settings?type=${type}`, {
          credentials: 'include' // Include cookies for authentication
        });
        if (!response.ok) {
          throw new Error(`Failed to fetch ${type} settings`);
        }
        return response.json() as Promise<AppSetting[]>;
      }
    });
  });
  
  // Calculate overall loading state
  const isLoading = queryResults.some(query => query.isLoading);
  
  // When queries complete, update the settings by type
  useEffect(() => {
    if (!isLoading) {
      const newSettingsByType: Record<string, AppSetting[]> = {};
      
      SETTING_TYPES.forEach((type, index) => {
        const query = queryResults[index];
        if (query.data) {
          newSettingsByType[type] = query.data;
        }
      });
      
      setSettingsByType(newSettingsByType);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, ...queryResults.map(q => q.data)]);
  
  // Check for errors
  useEffect(() => {
    const firstError = queryResults.find(q => q.error)?.error;
    if (firstError) {
      setError(firstError instanceof Error ? firstError : new Error(String(firstError)));
    } else {
      setError(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Helper function to get settings by type (deduplicates by value, case-insensitive)
  const getSettingsByType = (type: string): AppSetting[] => {
    const raw = settingsByType[type] || [];
    return raw.reduce((acc: AppSetting[], setting) => {
      if (!acc.some(s => s.value.toLowerCase() === setting.value.toLowerCase())) {
        acc.push(setting);
      }
      return acc;
    }, []);
  };
  
  // Provide the context value
  const contextValue: AppSettingsContextType = {
    categories: settingsByType.category || [],
    billingCycles: settingsByType.billingCycle || [],
    statuses: settingsByType.status || [],
    cardTypes: settingsByType.cardType || [],
    cardProviders: settingsByType.cardProvider || [],
    currencies: settingsByType.currency || [],
    getSettingsByType,
    isLoading,
    error
  };
  
  return (
    <AppSettingsContext.Provider value={contextValue}>
      {children}
    </AppSettingsContext.Provider>
  );
}

// Custom hook to use app settings
export function useAppSettings() {
  const context = useContext(AppSettingsContext);
  if (context === undefined) {
    throw new Error('useAppSettings must be used within an AppSettingsProvider');
  }
  return context;
}
